import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  Edit3, Save, X, Plus, Trash2, Settings, 
  Clock, Wrench, TestTube, CheckCircle, Eye,
  Star, Heart, Zap, Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusManager = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStatus, setNewStatus] = useState({
    key: '',
    label: '',
    color: 'bg-gray-500',
    icon: 'Clock'
  });

  const colorOptions = [
    { value: 'bg-blue-500', label: 'Blu', color: '#3B82F6' },
    { value: 'bg-green-500', label: 'E gjelbër', color: '#22C55E' },
    { value: 'bg-red-500', label: 'E kuqe', color: '#EF4444' },
    { value: 'bg-yellow-500', label: 'Të verdhë', color: '#EAB308' },
    { value: 'bg-purple-500', label: 'Vjollcë', color: '#A855F7' },
    { value: 'bg-orange-500', label: 'Portokalli', color: '#F97316' },
    { value: 'bg-pink-500', label: 'Rozë', color: '#EC4899' },
    { value: 'bg-indigo-500', label: 'Indigo', color: '#6366F1' },
    { value: 'bg-teal-500', label: 'Teal', color: '#14B8A6' },
    { value: 'bg-cyan-500', label: 'Cyan', color: '#06B6D4' },
    { value: 'bg-emerald-500', label: 'Smerald', color: '#10B981' },
    { value: 'bg-lime-500', label: 'Lime', color: '#84CC16' },
    { value: 'bg-amber-500', label: 'Amber', color: '#F59E0B' },
    { value: 'bg-rose-500', label: 'Rose', color: '#F43F5E' },
    { value: 'bg-violet-500', label: 'Violet', color: '#8B5CF6' },
    { value: 'bg-slate-500', label: 'Slate', color: '#64748B' },
    { value: 'bg-gray-500', label: 'Gri', color: '#6B7280' },
    { value: 'bg-zinc-500', label: 'Zinc', color: '#71717A' },
    { value: 'bg-neutral-500', label: 'Neutral', color: '#737373' },
    { value: 'bg-stone-500', label: 'Stone', color: '#78716C' }
  ];

  const iconOptions = [
    { value: 'Clock', label: 'Ora', component: Clock },
    { value: 'Wrench', label: 'Çelësi', component: Wrench },
    { value: 'TestTube', label: 'Test Tube', component: TestTube },
    { value: 'CheckCircle', label: 'Shenjë kontrolli', component: CheckCircle },
    { value: 'Eye', label: 'Syri', component: Eye },
    { value: 'Settings', label: 'Rregullimet', component: Settings },
    { value: 'Star', label: 'Ylli', component: Star },
    { value: 'Heart', label: 'Zemra', component: Heart },
    { value: 'Zap', label: 'Rrufeja', component: Zap },
    { value: 'Shield', label: 'Mburoja', component: Shield }
  ];

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/config/statuses`);
      setStatuses(response.data);
    } catch (error) {
      toast.error('Gabim në ngarkimin e statuseve');
      console.error('Fetch statuses error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (status) => {
    setEditingId(status.id);
  };

  const handleSave = async (status) => {
    try {
      await axios.put(`${API}/config/statuses/${status.id}`, {
        key: status.key,
        label: status.label,
        color: status.color,
        icon: status.icon
      });
      
      toast.success('Statusi u përditësua me sukses');
      setEditingId(null);
      fetchStatuses();
    } catch (error) {
      toast.error('Gabim në përditësimin e statusit');
      console.error('Update status error:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    fetchStatuses(); // Refresh to cancel any changes
  };

  const handleDelete = async (statusId) => {
    const confirmed = window.confirm('Jeni i sigurt që dëshironi të fshini këtë status? Kjo mund të ndikojë në tiketat ekzistuese.');
    
    if (!confirmed) return;

    try {
      await axios.delete(`${API}/config/statuses/${statusId}`);
      toast.success('Statusi u fshi me sukses');
      fetchStatuses();
    } catch (error) {
      toast.error('Gabim në fshirjen e statusit');
      console.error('Delete status error:', error);
    }
  };

  const handleAddNew = async () => {
    if (!newStatus.key || !newStatus.label) {
      toast.error('Plotësoni të gjitha fushat e detyrueshme');
      return;
    }

    try {
      await axios.post(`${API}/config/statuses`, newStatus);
      toast.success('Statusi i ri u shtua me sukses');
      setNewStatus({
        key: '',
        label: '',
        color: 'bg-gray-500',
        icon: 'Clock'
      });
      setShowAddForm(false);
      fetchStatuses();
    } catch (error) {
      toast.error('Gabim në shtimin e statusit të ri');
      console.error('Add status error:', error);
    }
  };

  const handleStatusChange = (statusId, field, value) => {
    setStatuses(prev => prev.map(status => 
      status.id === statusId ? { ...status, [field]: value } : status
    ));
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.component : Clock;
  };

  const getColorDisplay = (colorClass) => {
    const colorOption = colorOptions.find(opt => opt.value === colorClass);
    return colorOption ? colorOption.color : '#6B7280';
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        <p className="mt-2 text-gray-600">Duke ngarkuar statuset...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center">
                <Settings className="w-5 h-5 mr-2" />
                Menaxhimi i Statuseve
              </CardTitle>
              <CardDescription>
                Editoni, shtoni ose fshini statuset e tiketave me lehtësi
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddForm(!showAddForm)}>
              <Plus className="w-4 h-4 mr-2" />
              Shto Status të Ri
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Add New Status Form */}
          {showAddForm && (
            <Card className="mb-6 bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg">Shto Status të Ri</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="new-key">Çelësi (key) *</Label>
                    <Input
                      id="new-key"
                      value={newStatus.key}
                      onChange={(e) => setNewStatus(prev => ({ ...prev, key: e.target.value }))}
                      placeholder="ne_process"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="new-label">Etiketa *</Label>
                    <Input
                      id="new-label"
                      value={newStatus.label}
                      onChange={(e) => setNewStatus(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="Në Process"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="new-color">Ngjyra</Label>
                    <Select value={newStatus.color} onValueChange={(value) => setNewStatus(prev => ({ ...prev, color: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {colorOptions.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: color.color }}
                              ></div>
                              <span>{color.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="new-icon">Ikona</Label>
                    <Select value={newStatus.icon} onValueChange={(value) => setNewStatus(prev => ({ ...prev, icon: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {iconOptions.map((icon) => {
                          const IconComponent = icon.component;
                          return (
                            <SelectItem key={icon.value} value={icon.value}>
                              <div className="flex items-center space-x-2">
                                <IconComponent className="w-4 h-4" />
                                <span>{icon.label}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex space-x-3 mt-4">
                  <Button onClick={handleAddNew}>
                    <Save className="w-4 h-4 mr-2" />
                    Ruaj
                  </Button>
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>
                    <X className="w-4 h-4 mr-2" />
                    Anulo
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status List */}
          <div className="space-y-4">
            {statuses.map((status) => {
              const IconComponent = getIconComponent(status.icon);
              const isEditing = editingId === status.id;
              
              return (
                <Card key={status.id} className={`transition-all ${isEditing ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:shadow-md'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Icon Preview */}
                        <div className={`p-2 rounded-full ${status.color} text-white`}>
                          <IconComponent className="w-5 h-5" />
                        </div>
                        
                        {/* Status Info */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                          {isEditing ? (
                            <>
                              <div>
                                <Label className="text-xs">Çelësi</Label>
                                <Input
                                  value={status.key}
                                  onChange={(e) => handleStatusChange(status.id, 'key', e.target.value)}
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Etiketa</Label>
                                <Input
                                  value={status.label}
                                  onChange={(e) => handleStatusChange(status.id, 'label', e.target.value)}
                                  className="h-8"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Ngjyra</Label>
                                  <Select value={status.color} onValueChange={(value) => handleStatusChange(status.id, 'color', value)}>
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {colorOptions.map((color) => (
                                        <SelectItem key={color.value} value={color.value}>
                                          <div className="flex items-center space-x-2">
                                            <div 
                                              className="w-3 h-3 rounded-full" 
                                              style={{ backgroundColor: color.color }}
                                            ></div>
                                            <span className="text-xs">{color.label}</span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Ikona</Label>
                                  <Select value={status.icon} onValueChange={(value) => handleStatusChange(status.id, 'icon', value)}>
                                    <SelectTrigger className="h-8">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {iconOptions.map((icon) => {
                                        const IconComp = icon.component;
                                        return (
                                          <SelectItem key={icon.value} value={icon.value}>
                                            <div className="flex items-center space-x-2">
                                              <IconComp className="w-3 h-3" />
                                              <span className="text-xs">{icon.label}</span>
                                            </div>
                                          </SelectItem>
                                        );
                                      })}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <Label className="text-xs text-slate-500">Çelësi</Label>
                                <p className="font-mono text-sm">{status.key}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Etiketa</Label>
                                <p className="font-medium">{status.label}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Pamja</Label>
                                <Badge className={`${status.color} text-white`}>
                                  {status.label}
                                </Badge>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2 ml-4">
                        {isEditing ? (
                          <>
                            <Button size="sm" onClick={() => handleSave(status)}>
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleCancel}>
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleEdit(status)}>
                              <Edit3 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(status.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StatusManager;